import * as React from 'react';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import { grey } from '@mui/material/colors';

// Third Party
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';

// Internal
import { GET_PUBLICATIONS } from '../../data/graphql/queries';
import { FEATURE_PUBLICATION } from '../../data/graphql/mutations';
import { PublicationType } from '../../data/types';
import en from '../../data/en.json';

const DEFAULT_IMAGE = '/images/publishing-hero.jpg';

function PublicationRow({
  pub,
  onToggle,
}: {
  pub: PublicationType;
  onToggle: (pub: PublicationType) => void;
}) {
  const imgSrc = pub.coverMedia?.url || pub.coverPhotoUrl || DEFAULT_IMAGE;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        mb: 1,
        border: 1,
        borderColor: pub.featured ? 'primary.main' : grey[300],
        borderRadius: 1,
      }}
    >
      <Box
        component="img"
        src={imgSrc}
        alt={pub.title}
        sx={{ width: 64, height: 64, objectFit: 'cover', flexShrink: 0 }}
      />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {pub.title}
        </Typography>
        {pub.user?.profile?.nickname && (
          <Typography variant="caption" color={grey[600]}>
            By {pub.user.profile.nickname}
          </Typography>
        )}
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={!!pub.featured}
            onChange={() => onToggle(pub)}
            color="primary"
          />
        }
        label={pub.featured ? 'Featured' : 'Not featured'}
        labelPlacement="start"
      />
    </Box>
  );
}

export default function AdminFeaturedWork() {
  const [search, setSearch] = React.useState('');

  const { data: featuredData, refetch: refetchFeatured } = useQuery(GET_PUBLICATIONS, {
    variables: { paging_offset: 0, paging_limit: 100, search: '', tag_names: [], featured: true },
  });

  const [searchPublications, { data: searchData }] = useLazyQuery(GET_PUBLICATIONS);

  const [featurePublication] = useMutation(FEATURE_PUBLICATION);

  const featuredPublications: PublicationType[] = featuredData?.publications ?? [];
  const searchResults: PublicationType[] = searchData?.publications ?? [];

  const handleSearch = (value: string) => {
    setSearch(value);
    if (value.trim()) {
      searchPublications({
        variables: { paging_offset: 0, paging_limit: 20, search: value, tag_names: [] },
      });
    }
  };

  const handleToggle = async (pub: PublicationType) => {
    await featurePublication({
      variables: { uuid: pub.uuid, featured: !pub.featured },
    });
    refetchFeatured();
    if (search.trim()) {
      searchPublications({
        variables: { paging_offset: 0, paging_limit: 20, search, tag_names: [] },
      });
    }
  };

  const featuredUuids = new Set(featuredPublications.map((p) => p.uuid));

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
        Featured Work
      </Typography>
      <Typography variant="body2" color={grey[600]} sx={{ mb: 3 }}>
        Featured publications appear on the Digital Archive page.
      </Typography>

      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Currently Featured
      </Typography>
      {featuredPublications.length === 0 ? (
        <Typography variant="body2" color={grey[400]} sx={{ mb: 3 }}>
          No publications are featured yet.
        </Typography>
      ) : (
        <Box sx={{ mb: 3 }}>
          {featuredPublications.map((pub) => (
            <PublicationRow key={pub.uuid} pub={pub} onToggle={handleToggle} />
          ))}
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Search Publications
      </Typography>
      <TextField
        fullWidth
        placeholder="Search by title..."
        value={search}
        onChange={(e) => handleSearch(e.target.value)}
        size="small"
        sx={{ mb: 2 }}
      />
      {search.trim() && searchResults.length === 0 && (
        <Typography variant="body2" color={grey[400]}>
          No results found.
        </Typography>
      )}
      {searchResults
        .filter((pub) => !featuredUuids.has(pub.uuid))
        .map((pub) => (
          <PublicationRow key={pub.uuid} pub={pub} onToggle={handleToggle} />
        ))}
      {searchResults
        .filter((pub) => featuredUuids.has(pub.uuid))
        .map((pub) => (
          <PublicationRow
            key={pub.uuid}
            pub={{ ...pub, featured: true }}
            onToggle={handleToggle}
          />
        ))}
    </Box>
  );
}
